import { useEffect, useState } from 'react';
import { BookingShell } from './PublicBooking';
import GuestRequests from './GuestRequests';
import { bookingCalendar } from '../bookingCalendar';

async function readJson(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Operazione non riuscita');
  return payload;
}

export default function ManageBooking({ token }) {
  const [booking, setBooking] = useState(null);
  const [date, setDate] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [mode, setMode] = useState('view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let active = true;
    fetch(`/api/booking/manage?token=${encodeURIComponent(token)}`).then(readJson)
      .then(({ booking: row }) => {
        if (!active) return;
        setBooking(row); setDate(row.date); setPartySize(row.partySize);
      })
      .catch((requestError) => { if (active) setError(requestError.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  async function findSlots(event) {
    event.preventDefault(); setError(''); setNotice(''); setSelectedSlot(null); setLoading(true);
    try {
      const params = new URLSearchParams({ slug: booking.restaurantSlug, date, partySize: String(partySize) });
      const payload = await readJson(await fetch(`/api/booking/availability?${params}`));
      const availableSlots = payload.slots || [];
      const currentSlot = date === booking.date ? {
        shiftId: booking.shiftId,
        shiftName: 'Orario attuale',
        startTime: booking.startTime,
        endTime: booking.endTime,
      } : null;
      const includesCurrentSlot = availableSlots.some((slot) => slot.shiftId === currentSlot?.shiftId && slot.startTime === currentSlot?.startTime);
      const nextSlots = currentSlot && !includesCurrentSlot ? [currentSlot, ...availableSlots] : availableSlots;
      setSlots(nextSlots);
      if (!nextSlots.length) setError('Nessun orario disponibile per questa data.');
    } catch (requestError) { setSlots([]); setError(requestError.message); }
    finally { setLoading(false); }
  }

  async function updateBooking() {
    if (!selectedSlot) return;
    setSaving(true); setError('');
    try {
      const payload = await readJson(await fetch('/api/booking/manage', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action: 'update', date, partySize, shiftId: selectedSlot.shiftId, startTime: selectedSlot.startTime }),
      }));
      setBooking((current) => ({ ...current, ...payload.booking, shiftId: selectedSlot.shiftId }));
      setNotice('Prenotazione aggiornata. Il nuovo tavolo è confermato.'); setMode('view'); setSlots([]);
    } catch (requestError) { setError(requestError.message); }
    finally { setSaving(false); }
  }

  async function cancelBooking() {
    if (!window.confirm('Vuoi annullare definitivamente la prenotazione?')) return;
    setSaving(true); setError('');
    try {
      await readJson(await fetch('/api/booking/manage', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action: 'cancel' }),
      }));
      setBooking((current) => ({ ...current, status: 'cancellata' }));
      setNotice('Prenotazione annullata correttamente.'); setMode('view');
    } catch (requestError) { setError(requestError.message); }
    finally { setSaving(false); }
  }

  if (loading && !booking) return <BookingShell><div className="booking-loading"><span /><p>Carichiamo la prenotazione…</p></div></BookingShell>;
  if (!booking) return <BookingShell><div className="booking-success-icon error">!</div><h1>Collegamento non disponibile</h1><p className="booking-lead">{error || 'Il collegamento potrebbe essere scaduto.'}</p></BookingShell>;

  const cancelled = booking.status === 'cancellata';
  const calendar = bookingCalendar(booking);
  return (
    <BookingShell>
      <p className="eyebrow">GESTISCI PRENOTAZIONE</p><h1>{booking.restaurantName}</h1>
      <div className={`manage-booking-summary ${cancelled ? 'cancelled' : ''}`}>
        <span>{cancelled ? 'Prenotazione annullata' : 'Prenotazione confermata'}</span>
        <strong>{formatDate(booking.date)} · {booking.startTime}</strong>
        <small>{booking.partySize} {booking.partySize === 1 ? 'persona' : 'persone'} · {booking.firstName} {booking.lastName}</small>
      </div>
      {notice && <div className="form-alert success" role="status">{notice}</div>}
      {error && <div className="form-alert error" role="alert">{error}</div>}
      {!cancelled && mode === 'view' && <div className="manage-actions"><button className="button full" onClick={() => { setMode('edit'); setError(''); setNotice(''); }}>Modifica data o persone</button><button className="button button-danger full" disabled={saving} onClick={cancelBooking}>Annulla prenotazione</button></div>}
      {!cancelled && mode === 'edit' && (
        <div className="manage-edit">
          <form className="booking-form" onSubmit={findSlots}>
            <div className="form-row booking-search-row manage-booking-fields">
              <label>
                <span>Nuova data</span>
                <span className="booking-date-control">
                  <span aria-hidden="true">{date ? date.split('-').reverse().join('/') : 'gg/mm/aaaa'}</span>
                  <input aria-label="Nuova data" type="date" required value={date} onChange={(event) => { setDate(event.target.value); setSlots([]); }} />
                </span>
              </label>
              <label>
                <span>Persone</span>
                <input type="number" inputMode="numeric" min="1" max="50" required value={partySize} onChange={(event) => { setPartySize(Number(event.target.value)); setSlots([]); }} />
              </label>
            </div>
            <button className="button full" disabled={loading}>{loading ? 'Cerco…' : 'Cerca nuovi orari'}</button>
          </form>
          {!!slots.length && <div className="slot-section"><strong>Scegli il nuovo orario</strong><div className="slot-grid">{slots.map((slot) => <button type="button" className={selectedSlot?.shiftId === slot.shiftId && selectedSlot?.startTime === slot.startTime ? 'selected' : ''} key={`${slot.shiftId}-${slot.startTime}`} onClick={() => setSelectedSlot(slot)}><b>{slot.startTime}</b><small>{slot.shiftName}</small></button>)}</div></div>}
          <div className="manage-actions horizontal"><button className="button button-ghost" onClick={() => { setMode('view'); setSlots([]); setError(''); }}>Indietro</button><button className="button" disabled={!selectedSlot || saving} onClick={updateBooking}>{saving ? 'Aggiorno…' : 'Conferma modifica'}</button></div>
        </div>
      )}
      <div className="guest-actions"><a className="button button-ghost" href={`/menu/${encodeURIComponent(booking.restaurantSlug)}`}>Menù e informazioni</a>{calendar && <a className="button button-ghost" download="prenotazione-ristodaily.ics" href={`data:text/calendar;charset=utf-8,${encodeURIComponent(calendar)}`}>Aggiungi al calendario</a>}</div>
      {calendar && <p className="booking-privacy">L’evento nel calendario non si aggiorna automaticamente dopo modifiche o annullamento.</p>}
      {!cancelled && mode === 'view' && <GuestRequests token={token} />}
      {booking.restaurantPhone && <p className="booking-privacy">Se sei vicino all’orario della prenotazione, chiama <a href={`tel:${booking.restaurantPhone}`}>{booking.restaurantPhone}</a>.</p>}
    </BookingShell>
  );
}

function formatDate(date) { return new Date(`${date}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }); }
